import {
  InitialQuickProduct,
  VariantAttribute,
  VariantAttributeValue,
  getTaxExclusiveMarkup,
  getTaxInclusiveMarkup,
  makeVariant,
  makeVariantAttribute,
} from '@vend/business-components'
import { ApiProduct, ApiTax } from '@vend/types'
import { add, round } from 'vend-number'

export const getExistingVariantAttributes = (
  parentProduct: ApiProduct,
  variantProducts: ApiProduct[]
) => {
  const attributeMap: {
    [name: string]: { id: string; values: string[] }
  } = {}
  parentProduct.variant_options.forEach(
    option =>
      (attributeMap[option.name] = { id: option.id, values: [option.value] })
  )
  variantProducts.forEach(variant =>
    variant.variant_options.forEach(option => {
      attributeMap[option.name]?.values.push(option.value)
    })
  )

  return Object.entries(attributeMap).map(([name, { id, values }]) =>
    makeVariantAttribute({ name, id }, [...new Set(values)])
  )
}

export const pushNewValuesToExistingVariantAttribute = (
  existingVariantAttributes: VariantAttribute[],
  newVariantAttribute: VariantAttribute
) =>
  existingVariantAttributes.map(attr => {
    if (attr.attribute.name === newVariantAttribute.attribute.name) {
      return {
        ...attr,
        values: [...new Set([...attr.values, ...newVariantAttribute.values])],
      }
    }
    return attr
  })

export const getRestorableGapVariantCombos = (
  existingVariantAttributes: VariantAttribute[],
  newVariantAttribute: VariantAttribute,
  selectedAttributeValues: VariantAttributeValue[]
): VariantAttributeValue[][] | undefined => {
  const matchingAttribute = existingVariantAttributes.find(
    attr => attr.attribute.name === newVariantAttribute.attribute.name
  )

  if (matchingAttribute) {
    const restorableValues = newVariantAttribute.values.filter(value =>
      matchingAttribute.values.includes(value)
    )

    return restorableValues.length > 0
      ? restorableValues.map(value => [
          ...selectedAttributeValues,
          {
            value,
            definition: matchingAttribute.attribute,
          },
        ])
      : undefined
  }

  return undefined
}

export const getTaxRateByGroup = (taxGroup: ApiTax | undefined) =>
  taxGroup?.rates.reduce(
    (total, currentRate) => add(total, currentRate.rate),
    0
  ) || 0

export const convertVariantsToInitialQuickProduct = (
  parentProduct: ApiProduct,
  variantProducts: ApiProduct[],
  isTaxExclusive: boolean,
  variantTaxRateMap: { [id: string]: number },
  variantAttributes: VariantAttribute[]
): InitialQuickProduct => {
  return {
    id: parentProduct.id,
    name: parentProduct.name,
    description: parentProduct.description ?? undefined,
    type: 'Variant',
    variantAttributes,
    supplier: parentProduct.supplier_id
      ? {
          id: parentProduct.supplier_id,
          name: parentProduct.supplier?.name ?? '',
        }
      : undefined,
    variants: variantProducts.map(variant => {
      const supplyPrice = String(variant.supply_price || 0)
      const retailPrice = String(
        isTaxExclusive
          ? variant.price_excluding_tax || 0
          : variant.price_including_tax || 0
      )
      const markup = isTaxExclusive
        ? getTaxExclusiveMarkup(retailPrice, supplyPrice)
        : getTaxInclusiveMarkup(
            retailPrice,
            variantTaxRateMap[variant.id] || 0,
            supplyPrice
          )

      return makeVariant({
        id: variant.id || parentProduct.id,
        selected: false,
        hasCustomSku: Boolean(variant.product_codes.length),
        sku: variant.product_codes[0]?.code,
        skuType: variant.product_codes[0]?.type,
        retailPrice: round(retailPrice, 2),
        supplyPrice: round(supplyPrice, 2),
        markup,
        attributeValues: variant.variant_options.map(attrValue => ({
          value: attrValue.value,
          definition: { id: attrValue.id, name: attrValue.name },
        })),
        images: variant.skuImages.map(image => ({
          id: image.id,
          url: image.url,
        })),
        isExisting: true,
      })
    }),
  }
}
